// app/app/services/api.ts
import {
  uploadSessionManager,
  type UploadSession,
} from "./uploadSessionManager";
import { loggerUtils } from "./logger";
import { HttpClient, HttpError } from "./http";
import { tusUploadService, TusUploadService } from "./tusUpload";
import { config } from "../config";

const API_BASE_URL = config.api.baseUrl;

// Types matching backend responses
export interface Project {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Bucket {
  id: number;
  name: string;
  description: string | null;
  project_id: number;
  created_at: string;
}

export interface FileRecord {
  id: number;
  filename: string;
  size: number;
  mime_type: string | null;
  blob_hash: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  // optional fields that server may include
  folder_id?: number;
}

// Note: We don't alias File to FileRecord because it shadows the native browser File type
// Use FileRecord for server-side file metadata, and native File for browser uploads

export interface PresignedUrlResponse {
  download_url: string;
  expires_at: number;
  expires_in_seconds: number;
}

export interface Folder {
  id: number;
  name: string;
  bucket_id: number;
  parent_folder_id: number | null;
  created_at: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * A small helper to safely stringify unknown values for logs, avoiding circular ref errors.
 */
function safeStringify(value: unknown, maxLen?: number): string | undefined {
  if (value === undefined) return undefined;
  try {
    const s = typeof value === "string" ? value : JSON.stringify(value);
    return typeof maxLen === "number" ? s.substring(0, maxLen) : s;
  } catch {
    try {
      return String(value);
    } catch {
      return "[unserializable]";
    }
  }
}

/** Generic API service class */
class ApiService {
  private csrfToken: string | null = null;
  private http = new HttpClient(15000);

  constructor() {
    // Wire up periodic server sync for upload sessions
    uploadSessionManager.setUploadStatusCallback((uploadId) =>
      this.getUploadStatus(uploadId),
    );

    // Inject common headers and CSRF via request interceptor; log request start
    this.http.useRequest(async ({ url, init }) => {
      const baseHeaders: Record<string, string> = {
        Accept: "application/json",
        "X-Client": "stash-web",
      };
      // Preserve existing headers
      const merged = new Headers(init.headers as HeadersInit);
      for (const [k, v] of Object.entries(baseHeaders)) {
        if (!merged.has(k)) merged.set(k, v);
      }
      if (this.csrfToken && !merged.has("X-CSRF-Token")) {
        merged.set("X-CSRF-Token", this.csrfToken);
      }
      // mark start time for duration logging
      (init as Record<string, unknown>).__start = performance.now();
      const method = ((init.method as string) || "GET").toUpperCase();
      const endpoint = (init as Record<string, unknown>).__endpoint as string | undefined;
      const endpointPath = endpoint ?? new URL(url).pathname;
      
      // Never log request bodies for auth endpoints (contain passwords/tokens)
      const isAuthEndpoint = endpointPath.includes("/auth/");
      const bodyStr = !isAuthEndpoint && typeof init.body === 'string' ? 
        (() => { try { return JSON.stringify(JSON.parse(init.body as string)).slice(0, 500); } catch { return (init.body as string).slice(0, 500); } })() : 
        undefined;
      
      // Log request start (without sensitive bodies)
      try {
        this.safeLogRequest(method, endpointPath, bodyStr);
      // eslint-disable-next-line no-empty
      } catch {}
      return { url, init: { ...init, headers: merged } };
    });

    // Log responses centrally
    this.http.useResponse(async ({ url, init, response }) => {
      const start = (init as Record<string, unknown>).__start as number | undefined;
      const method = ((init.method as string) || "GET").toUpperCase();
      const endpoint = (init as Record<string, unknown>).__endpoint as string | undefined;
      const duration = start ? Math.round(performance.now() - start) : undefined;
      try {
        this.safeLogResponse(method, endpoint ?? new URL(url).pathname, response.status, duration);
      // eslint-disable-next-line no-empty
      } catch {}
      return response;
    });
  }

  /**
   * Generic typed request helper.
   * Uses generics to avoid `any` and to give callers type-safety.
   */
  private async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const method = (options.method || "GET").toUpperCase();
    const url = `${API_BASE_URL}${endpoint}`;
    // attach endpoint/method for interceptors
    const parsedBody = typeof options.body === "string" ? options.body : undefined;

    // Build headers (ensure we don't accidentally accept non-string header values)
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    try {
      let response: Response;
      try {
        type InternalInit = RequestInit & { __endpoint?: string; __method?: string };
        const init: InternalInit = { ...options, credentials: "include", headers };
        init.__endpoint = endpoint;
        init.__method = method;
        response = await this.http.request(url, init);
      } catch (err) {
        // Handle 401 errors - backend middleware already tried refresh, so if we get 401, session is dead
        if (err instanceof HttpError && err.status === 401) {
          loggerUtils.apiResponse(method, endpoint, 401, undefined);

          const wasAuthenticated = this.csrfToken !== null;
          const isAuthEndpoint = endpoint.includes("/auth/");

          // Clear client-side auth state
          this.csrfToken = null;

          // Dispatch logout event if user was authenticated (except for auth endpoints like login)
          if (wasAuthenticated && !isAuthEndpoint) {
            loggerUtils.auth("auth_error", { error: "Session expired", endpoint });
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('auth:logout', {
                detail: { reason: 'session_expired', endpoint }
              }));
            }
          }

          throw new HttpError(401, url, "Session expired - please log in again");
        } else {
          throw err;
        }
      }

      // Non-OK handled by HttpClient via HttpError

      // Try to parse JSON, but be defensive if body is empty
      const contentType = response.headers.get("content-type") || "";
      const data: unknown = contentType.includes("application/json")
        ? await response.json().catch(() => null)
        : await response.text().catch(() => null);
      
      // Never log response data for auth endpoints (contain CSRF tokens)
      const isAuthEndpoint = endpoint.includes("/auth/");
      const responseData = !isAuthEndpoint ? safeStringify(data) : undefined;
      loggerUtils.apiResponse(method, endpoint, response.status, undefined, responseData);

      return data as T;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        loggerUtils.error(
          error instanceof Error ? error : new Error(String(error)),
          "api_network",
          {
            endpoint,
            method,
          },
        );
      }
      throw error;
    }
  }

  private safeLogRequest(method: string, endpoint: string, body?: string) {
    loggerUtils.apiRequest(method, endpoint, safeStringify(body, 500));
  }

  private safeLogResponse(method: string, endpoint: string, status: number, duration?: number) {
    loggerUtils.apiResponse(method, endpoint, status, duration);
  }


  // Auth endpoints
  async login(
    email: string,
    password: string,
  ): Promise<{ csrf_token: string; user_id: string; is_admin: boolean; jwt_expires_in_seconds: number }> {
    loggerUtils.auth("login", { email });

    const result = await this.request<{
      csrf_token: string;
      user_id: string;
      is_admin: boolean;
      jwt_expires_in_seconds: number;
      totp_enabled: boolean;
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });

    // Store CSRF token for subsequent requests
    this.csrfToken = result.csrf_token;

    // No need to track expiry - backend middleware handles token refresh automatically
    loggerUtils.auth("login_success", {
      user_id: result.user_id,
      jwt_ttl: result.jwt_expires_in_seconds,
    });

    return result;
  }

  async logout(): Promise<void> {
    loggerUtils.auth("logout");
    await this.request("/auth/logout", { method: "POST" });
    this.csrfToken = null;
    loggerUtils.auth("logout", { success: true });
  }

  async getCurrentUser(): Promise<{
    id: string;
    email: string;
    is_admin: boolean;
    avatar_url?: string;
  }> {
    return this.request("/auth/me");
  }

  async checkSession(): Promise<{
    authenticated: boolean;
    user: unknown;
    csrf_token?: string;
    jwt_expires_in_seconds?: number;
  }> {
    const session = await this.request<{
      authenticated: boolean;
      user: unknown;
      csrf_token?: string;
      jwt_expires_in_seconds?: number;
    }>("/auth/session");

    // Update CSRF token if provided (middleware may have refreshed it)
    if (session.csrf_token) {
      this.csrfToken = session.csrf_token;
    }

    return session;
  }


  // Project endpoints
  async getProjects(): Promise<Project[]> {
    const data = await this.request<unknown>("/v1/projects");
    return this.ensureProjects(data);
  }

  async getProject(id: number): Promise<Project> {
    const data = await this.request<unknown>(`/v1/projects/${id}`);
    return this.ensureProject(data);
  }

  async createProject(name: string, description?: string): Promise<Project> {
    const data = await this.request<unknown>("/v1/projects", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
    return this.ensureProject(data);
  }

  async updateProject(
    id: number,
    name: string,
    description?: string,
  ): Promise<Project> {
    return this.request<Project>(`/v1/projects/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name, description }),
    });
  }

  async deleteProject(id: number): Promise<void> {
    return this.request(`/v1/projects/${id}`, { method: "DELETE" });
  }

  // Bucket endpoints
  async getBuckets(): Promise<Bucket[]> {
    const data = await this.request<unknown>("/v1/buckets");
    return this.ensureBuckets(data);
  }

  async getBucketsForProject(projectId: number): Promise<Bucket[]> {
    const buckets = await this.getBuckets();
    
    // All users (including admins) see only buckets from the specific project
    // The difference is that admins have access to ALL buckets via getBuckets(),
    // while normal users only get buckets they have access to
    return buckets.filter((bucket) => bucket.project_id === projectId);
  }

  async createBucket(
    name: string,
    description?: string,
    projectId?: number,
  ): Promise<Bucket> {
    const data = await this.request<unknown>("/v1/buckets", {
      method: "POST",
      body: JSON.stringify({
        name,
        description: description || null,
        project_id: projectId,
      }),
    });
    return this.ensureBucket(data);
  }

  async updateBucket(
    id: number,
    name: string,
    description?: string,
  ): Promise<Bucket> {
    const data = await this.request<unknown>(`/v1/buckets/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name, description }),
    });
    return this.ensureBucket(data);
  }

  async deleteBucket(id: number): Promise<void> {
    return this.request(`/v1/buckets/${id}`, { method: "DELETE" });
  }

  // Folder endpoints
  async getFolders(bucketId: number, parentFolderId?: number | null): Promise<Folder[]> {
    const query = new URLSearchParams();
    query.set("bucket_id", String(bucketId));
    if (parentFolderId !== undefined && parentFolderId !== null) {
      query.set("parent_folder_id", String(parentFolderId));
    }
    return this.request(`/v1/folders?${query.toString()}`);
  }

  async getAllFolders(bucketId: number): Promise<Folder[]> {
    const query = new URLSearchParams();
    query.set("bucket_id", String(bucketId));
    query.set("all", "true");
    return this.request(`/v1/folders?${query.toString()}`);
  }

  async createFolder(name: string, bucketId: number, parentFolderId?: number | null): Promise<Folder> {
    return this.request("/v1/folders", {
      method: "POST",
      body: JSON.stringify({
        name,
        bucket_id: bucketId,
        parent_folder_id: parentFolderId || null,
      }),
    });
  }

  async getFolder(id: number): Promise<Folder> {
    return this.request(`/v1/folders/${id}`);
  }

  async deleteFolder(id: number): Promise<void> {
    return this.request(`/v1/folders/${id}`, { method: "DELETE" });
  }

  // File endpoints
  async getFiles(params?: {
    bucketId?: number;
    page?: number;
    limit?: number;
  }): Promise<{
    files: FileRecord[];
    total: number;
    page: number;
    limit: number;
  }> {
    const query = new URLSearchParams();
    if (params?.bucketId) query.set("bucket_id", String(params.bucketId));
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.toString();
    return this.request(`/v1/files${qs ? `?${qs}` : ""}`) as Promise<{
      files: FileRecord[];
      total: number;
      page: number;
      limit: number;
    }>;
  }

  async searchFiles(searchQuery: string, limit: number = 20): Promise<FileRecord[]> {
    // Since backend doesn't support search yet, we'll fetch files from all buckets
    // and filter client-side
    try {
      // Get all buckets the user has access to
      const buckets = await this.getBuckets();

      // Fetch files from all buckets in parallel
      const filePromises = buckets.map(bucket =>
        this.getFiles({ bucketId: bucket.id, limit: 100 }).catch(() => ({ files: [], total: 0, page: 1, limit: 100 }))
      );

      const fileResults = await Promise.all(filePromises);

      // Combine all files
      const allFiles = fileResults.flatMap(result => result.files);

      // Filter by search query (case-insensitive filename match)
      const searchLower = searchQuery.toLowerCase();
      const matchedFiles = allFiles.filter(file =>
        file.filename.toLowerCase().includes(searchLower)
      );

      // Sort by creation date (newest first)
      matchedFiles.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      // Return limited results
      return matchedFiles.slice(0, limit);
    } catch (error) {
      loggerUtils.error(
        error instanceof Error ? error : new Error(String(error)),
        'search_files_error',
        { query: searchQuery }
      );
      return [];
    }
  }

  async getFilesInBucket(bucketId: number): Promise<FileRecord[]> {
    const res = await this.getFiles({ bucketId });
    return res.files;
  }

  async getFilesInFolder(folderId: number): Promise<FileRecord[]> {
    const res = await this.getFiles();
    return res.files.filter((file) => file.folder_id === folderId);
  }

  // Storage usage API
  async getUserStorageUsage(): Promise<{
    total_bytes: number;
    total_bytes_human: string;
    file_count: number;
    unique_blob_count: number;
    deleted_bytes: number;
    deleted_bytes_human: string;
    deleted_file_count: number;
  }> {
    return this.request('/v1/storage/usage/user');
  }

  // Helper methods for data aggregation
  async getProjectStats(projectId: number): Promise<{
    bucketCount: number;
    totalFiles: number;
    totalSize: string;
    totalSizeBytes: number;
    avgFileSize: string;
    largestBucket?: {
      name: string;
      size: string;
      fileCount: number;
    };
  }> {
    const buckets = await this.getBucketsForProject(projectId);
    const bucketStatsPromises = buckets.map((bucket) =>
      this.getBucketStats(bucket.id),
    );
    const bucketStats = await Promise.all(bucketStatsPromises);

    const totalFiles = bucketStats.reduce(
      (sum, stats) => sum + stats.fileCount,
      0,
    );
    const totalSizeBytes = bucketStats.reduce((sum, stats) => {
      const sizeStr = stats.totalSize;
      const match = sizeStr.match(/^([\d.]+)\s*([KMGT]?B)$/);
      if (!match) return sum;
      const [, num, unit] = match;
      const bytes = parseFloat(num);
      const multipliers: Record<string, number> = {
        B: 1,
        KB: 1024,
        MB: 1024 ** 2,
        GB: 1024 ** 3,
        TB: 1024 ** 4,
      };
      return sum + bytes * (multipliers[unit] || 1);
    }, 0);

    // Find largest bucket
    let largestBucket: { name: string; size: string; fileCount: number } | undefined;
    let maxSize = 0;
    
    for (let i = 0; i < buckets.length; i++) {
      const bucket = buckets[i];
      const stats = bucketStats[i];
      const sizeStr = stats.totalSize;
      const match = sizeStr.match(/^([\d.]+)\s*([KMGT]?B)$/);
      
      if (match) {
        const [, num, unit] = match;
        const bytes = parseFloat(num);
        const multipliers: Record<string, number> = {
          B: 1,
          KB: 1024,
          MB: 1024 ** 2,
          GB: 1024 ** 3,
          TB: 1024 ** 4,
        };
        const bucketBytes = bytes * (multipliers[unit] || 1);
        
        if (bucketBytes > maxSize) {
          maxSize = bucketBytes;
          largestBucket = {
            name: bucket.name,
            size: stats.totalSize,
            fileCount: stats.fileCount
          };
        }
      }
    }

    const avgFileSize = totalFiles > 0 ? this.formatFileSize(totalSizeBytes / totalFiles) : "0 B";

    return {
      bucketCount: buckets.length,
      totalFiles,
      totalSize: this.formatFileSize(totalSizeBytes),
      totalSizeBytes,
      avgFileSize,
      largestBucket,
    };
  }

  async getBucketStats(bucketId: number): Promise<{
    fileCount: number;
    totalSize: string;
  }> {
    const files = await this.getFilesInBucket(bucketId);
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);

    return {
      fileCount: files.length,
      totalSize: this.formatFileSize(totalSize),
    };
  }

  // Utility methods
  private ensureProject(data: unknown): Project {
    if (!data || typeof data !== 'object') throw new Error('Invalid project response');
    const r = data as Record<string, unknown>;
    const id = r.id;
    const name = r.name;
    const description = 'description' in r ? (r.description as string | null) : null;
    const created_at = r.created_at;
    const updated_at = r.updated_at;
    if (typeof id !== 'number' || typeof name !== 'string' || typeof created_at !== 'string' || typeof updated_at !== 'string') {
      throw new Error('Invalid project shape');
    }
    return { id, name, description, created_at, updated_at } as Project;
  }

  private ensureProjects(data: unknown): Project[] {
    if (!Array.isArray(data)) throw new Error('Invalid projects response');
    return data.map((d) => this.ensureProject(d));
  }

  private ensureBucket(data: unknown): Bucket {
    if (!data || typeof data !== 'object') throw new Error('Invalid bucket response');
    const r = data as Record<string, unknown>;
    const id = r.id;
    const name = r.name;
    const description = 'description' in r ? (r.description as string | null) : null;
    const project_id = r.project_id;
    const created_at = r.created_at;
    if (typeof id !== 'number' || typeof name !== 'string' || typeof project_id !== 'number' || typeof created_at !== 'string') {
      throw new Error('Invalid bucket shape');
    }
    return { id, name, description, project_id, created_at } as Bucket;
  }

  private ensureBuckets(data: unknown): Bucket[] {
    if (!Array.isArray(data)) throw new Error('Invalid buckets response');
    return data.map((d) => this.ensureBucket(d));
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }

  // Chunked Upload methods
  async initiateChunkedUpload(
    filename: string,
    bucketId: number,
    totalSize: number,
    folderId?: number | null,
  ): Promise<{
    upload_id: string;
    upload_url?: string;
    chunk_size: number;
    expires_at: string;
  }> {
    const response = await this.request("/v1/files/chunked-upload/initiate", {
      method: "POST",
      body: JSON.stringify({
        filename,
        bucket_id: bucketId,
        total_size: totalSize,
        ...(folderId ? { folder_id: folderId } : {}),
      }),
    });
    
    // Extract data from response structure that matches server response
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      return {
        upload_id: data.upload_id,
        upload_url: data.upload_url,
        chunk_size: data.chunk_size,
        expires_at: data.expires_at,
      };
    }
    
    // Fallback to direct response for backward compatibility
    return response as {
      upload_id: string;
      upload_url?: string;
      chunk_size: number;
      expires_at: string;
    };
  }

  async uploadChunk(
    uploadId: string,
    chunkIndex: number,
    chunkSize: number,
    chunkData: ArrayBuffer,
  ): Promise<{
    upload_id: string;
    uploaded_bytes: number;
    total_bytes: number;
    completed: boolean;
    file_id?: number;
  }> {
    // Build headers with CSRF token
    const headers: Record<string, string> = {
      "Content-Type": "application/octet-stream",
    };
    
    // Add CSRF token if available
    if (this.csrfToken) {
      headers["X-CSRF-Token"] = this.csrfToken;
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout per chunk
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/files/chunked-upload/${uploadId}?chunk_index=${chunkIndex}&chunk_size=${chunkSize}`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: chunkData,
          signal: controller.signal,
        },
      );
      
      clearTimeout(timeoutId);

    if (!response.ok) {
      // Try to extract detailed error information from server
      let errorMessage = `Chunk upload failed: ${response.status} ${response.statusText}`;
      let errorDetails = "";
      let errorCode = "";
      
      const ct = response.headers.get("content-type") || "";
      try {
        if (ct.includes("application/json")) {
          const errorData = await response.json();
          if (errorData && typeof errorData === "object") {
            const r = errorData as Record<string, unknown>;
            
            // Extract structured error information
            if (typeof r.error === "string" && r.error) {
              errorMessage = r.error;
            }
            if (typeof r.details === "string" && r.details) {
              errorDetails = r.details;
            }
            if (typeof r.error_code === "string" && r.error_code) {
              errorCode = r.error_code;
            }
          }
        } else {
          const textError = await response.text();
          if (textError) {
            errorDetails = textError;
          }
        }
      } catch {
        // ignore parse errors - use original message
      }
      
      // Construct comprehensive error message
      let finalMessage = errorMessage;
      if (errorDetails) {
        finalMessage += ` (${errorDetails})`;
      }
      if (errorCode) {
        finalMessage += ` [${errorCode}]`;
      }
      
      throw new ApiError(response.status, finalMessage);
    }

      const jsonResponse = await response.json();
      
      // Handle structured response with data field
      if (jsonResponse && typeof jsonResponse === 'object' && 'data' in jsonResponse) {
        return jsonResponse.data;
      }
      
      // Fallback to direct response
      return jsonResponse;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(408, 'Chunk upload timeout - chunk took too long to upload');
      }
      throw error;
    }
  }

  async getUploadStatus(uploadId: string): Promise<{
    upload_id: string;
    uploaded_bytes: number;
    total_bytes: number;
    completed: boolean;
    file_id?: number;
  }> {
    const response = await this.request(
      `/v1/files/chunked-upload/${uploadId}/status?upload_id=${uploadId}`,
    );
    
    // Handle structured response with data field
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as any).data;
      return {
        upload_id: data.upload_id,
        uploaded_bytes: data.uploaded_bytes,
        total_bytes: data.total_bytes,
        completed: data.completed,
        file_id: data.file_id,
      };
    }
    
    // Fallback to direct response
    return response as {
      upload_id: string;
      uploaded_bytes: number;
      total_bytes: number;
      completed: boolean;
      file_id?: number;
    };
  }

  async cancelUpload(uploadId: string): Promise<void> {
    return this.request(`/v1/files/chunked-upload/${uploadId}/cancel`, {
      method: "DELETE",
    });
  }

  // Small type used internally for chunk results
  private typeChunkResult(result: unknown): {
    upload_id: string;
    uploaded_bytes: number;
    total_bytes: number;
    completed: boolean;
    file_id?: number;
  } | null {
    if (!result || typeof result !== "object") return null;
    // Best-effort runtime validation
    const typedRes = result as Record<string, unknown>;
    if (
      typeof typedRes.uploaded_bytes === "number" &&
      typeof typedRes.total_bytes === "number"
    ) {
      return {
        upload_id: String(typedRes.upload_id),
        uploaded_bytes: Number(typedRes.uploaded_bytes),
        total_bytes: Number(typedRes.total_bytes),
        completed: Boolean(typedRes.completed),
        file_id:
          typeof typedRes.file_id === "number" ? typedRes.file_id : undefined,
      };
    }
    return null;
  }

  // Unified upload methods with retries and AbortController support
  private async retryOperation<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000,
    abortSignal?: AbortSignal,
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (abortSignal?.aborted) {
        throw new Error("Operation aborted");
      }

      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // If it's an ApiError, decide whether to retry
        if (lastError instanceof ApiError) {
          if ([400, 401, 403, 404, 413, 422].includes(lastError.status)) {
            throw lastError;
          }
          if (
            [502, 503, 504].includes(lastError.status) &&
            attempt < maxRetries
          ) {
            const serverErrorDelay = Math.min(
              baseDelay * Math.pow(2, attempt) * 2,
              30000,
            );
            const jitter = Math.random() * 2000;
            await new Promise((r) => setTimeout(r, serverErrorDelay + jitter));
            continue;
          }
        }

        if (attempt === maxRetries) break;

        const exponentialDelay = baseDelay * Math.pow(1.5, attempt);
        const jitter = Math.random() * baseDelay * 0.5;
        const cappedDelay = Math.min(exponentialDelay + jitter, 15000);

        loggerUtils.upload("error", "", undefined, {
          attempt: attempt + 1,
          retryDelay: Math.round(cappedDelay),
          error: lastError.message,
        });
        await new Promise((r) => setTimeout(r, cappedDelay));
      }
    }

    throw lastError ?? new Error("Unknown retry error");
  }

  async uploadFile(
    file: File,
    bucketId: number,
    options: {
      onProgress?: (
        progress: number,
        uploadedBytes: number,
        session?: UploadSession,
      ) => void;
      onStatusChange?: (
        status: UploadSession["status"],
        session?: UploadSession,
      ) => void;
      abortSignal?: AbortSignal;
      maxRetries?: number;
      chunkThreshold?: number;
      resumable?: boolean;
      sessionId?: string;
      folderId?: number | null;
    } = {},
  ): Promise<{ file_id: number; sessionId?: string }> {
    const {
      onProgress,
      onStatusChange,
      abortSignal,
      maxRetries = 3,
      chunkThreshold = 10 * 1024 * 1024,
      resumable = true,
      sessionId,
      folderId,
    } = options;

    const useChunked = file.size > chunkThreshold;
    const useTus = file.size > chunkThreshold && TusUploadService.isSupported(); // Use TUS for all chunked uploads (> 10MB)

    const method = useTus
      ? "tus"
      : useChunked && resumable
        ? "resumable"
        : useChunked
          ? "chunked"
          : "multipart";

    loggerUtils.upload("start", file.name, 0, {
      bucketId,
      fileSize: file.size,
      method,
      sessionId: sessionId || "new",
    });

    try {
      let result: { file_id: number; sessionId?: string };

      if (useTus) {
        // Use TUS protocol for large files
        result = await this.uploadFileTus(file, bucketId, {
          onProgress,
          abortSignal,
          folderId,
        });
      } else if (useChunked && resumable) {
        result = await this.uploadFileResumable(file, bucketId, {
          onProgress,
          onStatusChange,
          abortSignal,
          maxRetries,
          sessionId,
          folderId,
        });
      } else if (useChunked) {
        const chunkResult = await this.uploadFileChunked(file, bucketId, {
          onProgress,
          abortSignal,
          maxRetries,
          folderId,
        });
        result = { file_id: chunkResult.file_id };
      } else {
        const multipartResult = await this.uploadFileMultipart(file, bucketId, {
          onProgress,
          abortSignal,
          maxRetries,
          folderId,
        });
        result = { file_id: multipartResult.file_id };
      }

      loggerUtils.upload("success", file.name, 100, {
        bucketId,
        fileId: result.file_id,
        method,
        sessionId: result.sessionId,
      });

      return result;
    } catch (error) {
      loggerUtils.upload("error", file.name, undefined, {
        bucketId,
        method,
        error: error instanceof Error ? error.message : String(error),
        sessionId,
      });
      throw error;
    }
  }

  // --- Multipart upload (XHR) ---
  async uploadFileMultipart(
    file: File,
    bucketId: number,
    options: {
      onProgress?: (progress: number, uploadedBytes: number) => void;
      abortSignal?: AbortSignal;
      maxRetries?: number;
      folderId?: number | null;
    } = {},
  ): Promise<{ file_id: number }> {
    const { onProgress, abortSignal, maxRetries = 3, folderId } = options;

    const doUpload = () =>
      new Promise<{ file_id: number }>((resolve, reject) => {
        if (abortSignal?.aborted) {
          reject(new Error("Upload aborted"));
          return;
        }

        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append("file", file);
        formData.append("bucket_id", String(bucketId));
        if (folderId) {
          formData.append("folder_id", String(folderId));
        }

        const handleAbort = () => {
          xhr.abort();
          reject(new Error("Upload aborted"));
        };

        if (abortSignal) {
          abortSignal.addEventListener("abort", handleAbort);
        }

        // Track progress
        xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress, event.loaded);
          }
        });

        xhr.addEventListener("load", () => {
          if (abortSignal) {
            abortSignal.removeEventListener("abort", handleAbort);
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const parsed = JSON.parse(xhr.responseText);
              // Normalize to { file_id } regardless of server shape
              let fileId: number | undefined;
              if (parsed && typeof parsed === 'object') {
                if (typeof (parsed as any).file_id === 'number') fileId = (parsed as any).file_id;
                else if (typeof (parsed as any).id === 'number') fileId = (parsed as any).id;
                else if ((parsed as any).data && typeof (parsed as any).data === 'object' && typeof (parsed as any).data.id === 'number') fileId = (parsed as any).data.id;
              }

              if (typeof fileId === 'number') {
                resolve({ file_id: fileId });
              } else {
                reject(new ApiError(xhr.status, 'Upload succeeded but no file ID returned'));
              }
            } catch {
              reject(new ApiError(xhr.status, "Invalid response format"));
            }
          } else {
            let errorMessage = `Upload failed: ${xhr.status} ${xhr.statusText}`;
            let errorDetails = "";
            
            // Try to parse detailed error from server
            try {
              const responseText = xhr.responseText;
              if (responseText) {
                try {
                  const errorData = JSON.parse(responseText);
                  if (errorData && typeof errorData === "object") {
                    const r = errorData as Record<string, unknown>;
                    if (typeof r.error === "string") {
                      errorMessage = r.error;
                    }
                    if (typeof r.details === "string") {
                      errorDetails = r.details;
                    }
                    if (typeof r.error_code === "string") {
                      errorDetails += errorDetails ? ` [${r.error_code}]` : `[${r.error_code}]`;
                    }
                  }
                } catch {
                  // Not JSON, use as plain text details
                  errorDetails = responseText.substring(0, 200); // Limit length
                }
              }
            } catch {
              // ignore
            }
            
            // Add generic error explanations for common status codes if no specific details
            if (!errorDetails) {
              if (xhr.status === 502) errorDetails = "Server gateway error - upload likely too large or server timeout";
              else if (xhr.status === 413) errorDetails = "File exceeds maximum allowed size";
              else if (xhr.status === 504) errorDetails = "Upload timeout - file too large or network too slow";
              else if (xhr.status === 401) errorDetails = "Authentication failed - please login again";
              else if (xhr.status === 403) errorDetails = "Permission denied - you cannot upload to this bucket";
            }
            
            const finalMessage = errorDetails ? `${errorMessage} (${errorDetails})` : errorMessage;
            reject(new ApiError(xhr.status, finalMessage));
          }
        });

        xhr.addEventListener("error", () => {
          if (abortSignal) {
            abortSignal.removeEventListener("abort", handleAbort);
          }
          reject(new ApiError(0, "Network error - check your connection"));
        });

        xhr.addEventListener("timeout", () => {
          if (abortSignal) {
            abortSignal.removeEventListener("abort", handleAbort);
          }
          reject(new ApiError(0, "Upload timeout"));
        });

        if (this.csrfToken) {
          try {
            xhr.open("POST", `${API_BASE_URL}/v1/files/upload`);
            xhr.setRequestHeader("X-CSRF-Token", this.csrfToken);
          } catch {
            // open may fail in unusual environments
          }
        } else {
          xhr.open("POST", `${API_BASE_URL}/v1/files/upload`);
        }

        xhr.withCredentials = true;
        xhr.timeout = 600000; // 10 minute timeout for large files
        xhr.send(formData);
      });

    return this.retryOperation(doUpload, maxRetries, 1000, abortSignal);
  }

  // --- TUS Protocol upload (resumable standard) ---
  async uploadFileTus(
    file: File,
    bucketId: number,
    options: {
      onProgress?: (progress: number, uploadedBytes: number) => void;
      abortSignal?: AbortSignal;
      folderId?: number | null;
    } = {},
  ): Promise<{ file_id: number; sessionId: string }> {
    const { onProgress, abortSignal, folderId } = options;

    return new Promise(async (resolve, reject) => {
      const sessionId = await tusUploadService.startUpload({
        file,
        bucketId,
        onProgress: (bytesUploaded, bytesTotal) => {
          const progress = Math.round((bytesUploaded / bytesTotal) * 100);
          onProgress?.(progress, bytesUploaded);
        },
        onSuccess: (fileId) => {
          if (fileId && fileId > 0) {
            resolve({ file_id: fileId, sessionId });
          } else {
            // Upload completed but no file_id - this shouldn't happen in normal flow
            reject(new Error('Upload completed but file was not saved. Please try again or contact support.'));
          }
        },
        onError: (error) => {
          // Convert TUS errors to user-friendly messages
          let userMessage = 'Upload failed. Please try again.';

          if (error.message.includes('403') || error.message.includes('Forbidden')) {
            userMessage = 'You don\'t have permission to upload to this location.';
          } else if (error.message.includes('413') || error.message.includes('too large')) {
            userMessage = 'File is too large. Maximum size is 100GB.';
          } else if (error.message.includes('409') || error.message.includes('Conflict')) {
            userMessage = 'Upload conflict detected. Please refresh and try again.';
          } else if (error.message.includes('network') || error.message.includes('Network')) {
            userMessage = 'Network error. Please check your connection and try again.';
          } else if (error.message.includes('Failed to create file record')) {
            userMessage = 'Upload completed but failed to save. Please contact support.';
          }

          reject(new Error(userMessage));
        },
        abortSignal,
        metadata: {
          // Add CSRF token and folder_id to TUS metadata
          csrf_token: this.csrfToken || '',
          ...(folderId ? { folder_id: String(folderId) } : {}),
        },
      });
    });
  }

  // --- Chunked upload (parallel chunks) ---
  async uploadFileChunked(
    file: File,
    bucketId: number,
    options: {
      onProgress?: (progress: number, uploadedBytes: number) => void;
      abortSignal?: AbortSignal;
      maxRetries?: number;
      parallelChunks?: number;
      folderId?: number | null;
    } = {},
  ): Promise<{ file_id: number }> {
    const {
      onProgress,
      abortSignal,
      maxRetries = 3,
      parallelChunks = file.size > 10 * 1024 * 1024 * 1024 ? 4 : 6, // 6 parallel chunks for files < 10GB, 4 for larger
      folderId,
    } = options;

    return this.retryOperation(
      async () => {
        if (abortSignal?.aborted) {
          throw new Error("Upload aborted");
        }

        const initResponse = await this.initiateChunkedUpload(
          file.name,
          bucketId,
          file.size,
          folderId,
        );
        const chunkSize = initResponse.chunk_size;
        const totalChunks = Math.ceil(file.size / chunkSize);

        let uploadedChunks = 0;
        let totalUploadedBytes = 0;
        let lastResult: {
          uploaded_bytes: number;
          completed: boolean;
          file_id?: number;
          chunkIndex?: number;
        } | null = null;
        const chunkResults = new Map<
          number,
          {
            uploaded_bytes: number;
            total_bytes: number;
            completed: boolean;
            file_id?: number;
          }
        >();

        // Build queue
        const chunkQueue: Array<{
          index: number;
          start: number;
          size: number;
        }> = [];
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          chunkQueue.push({ index: i, start, size: end - start });
        }

        // Process batches
        while (chunkQueue.length > 0 && !abortSignal?.aborted) {
          const batch = chunkQueue.splice(0, parallelChunks);
          const chunkPromises = batch.map(async (chunkInfo) => {
            const slice = file.slice(
              chunkInfo.start,
              chunkInfo.start + chunkInfo.size,
            );
            const buffer = await slice.arrayBuffer();

            const res = await this.uploadChunkWithRetry(
              initResponse.upload_id,
              chunkInfo.index,
              chunkInfo.size,
              buffer,
              chunkInfo.index,
              maxRetries,
              abortSignal,
            );

            return { res, chunkInfo };
          });

          const batchResults = await Promise.all(chunkPromises);
          for (const { res, chunkInfo } of batchResults) {
            const typed = this.typeChunkResult(res);
            if (!typed) continue;

            chunkResults.set(chunkInfo.index, typed);
            uploadedChunks++;
            totalUploadedBytes = typed.uploaded_bytes ?? totalUploadedBytes;

            const progressPercent = Math.round(
              (uploadedChunks / totalChunks) * 100,
            );
            if (onProgress) onProgress(progressPercent, totalUploadedBytes);

            if (typed.completed) {
              lastResult = {
                uploaded_bytes: typed.uploaded_bytes,
                completed: typed.completed,
                file_id: typed.file_id,
                chunkIndex: chunkInfo.index,
              };
              break;
            }
          }

          if (lastResult?.completed) break;
        }

        if (abortSignal?.aborted) {
          await this.cancelUpload(initResponse.upload_id).catch(() => {});
          throw new Error("Upload aborted");
        }

        if (!lastResult?.completed || !lastResult?.file_id) {
          throw new ApiError(500, "Upload completed but no file ID received");
        }

        return { file_id: lastResult.file_id };
      },
      maxRetries,
      2000,
      options.abortSignal,
    );
  }

  private async uploadChunkWithRetry(
    uploadId: string,
    chunkIndex: number,
    chunkSize: number,
    chunkData: ArrayBuffer,
    originalChunkIndex: number,
    maxRetries: number = 3,
    abortSignal?: AbortSignal,
  ): Promise<
    {
      upload_id: string;
      uploaded_bytes: number;
      total_bytes: number;
      completed: boolean;
      file_id?: number;
    } & { chunkIndex?: number }
  > {
    return this.retryOperation(
      async () => {
        const result = await this.uploadChunk(
          uploadId,
          chunkIndex,
          chunkSize,
          chunkData,
        );
        return { ...result, chunkIndex: originalChunkIndex };
      },
      maxRetries,
      500 + Math.random() * 1000,
      abortSignal,
    );
  }

  // --- Resumable upload with session management ---
  async uploadFileResumable(
    file: File,
    bucketId: number,
    options: {
      onProgress?: (
        progress: number,
        uploadedBytes: number,
        session: UploadSession,
      ) => void;
      onStatusChange?: (
        status: UploadSession["status"],
        session: UploadSession,
      ) => void;
      abortSignal?: AbortSignal;
      maxRetries?: number;
      sessionId?: string;
      folderId?: number | null;
    } = {},
  ): Promise<{ file_id: number; sessionId: string }> {
    const {
      onProgress,
      onStatusChange,
      abortSignal,
      maxRetries = 3,
      sessionId,
      folderId,
    } = options;

    let session: UploadSession | undefined;
    let uploadInitiated = false;

    try {
      if (sessionId) {
        const existingSession = uploadSessionManager.getSession(sessionId);
        if (!existingSession)
          throw new ApiError(404, "Upload session not found");
        if (existingSession.status === "completed")
          throw new ApiError(400, "Upload already completed");
        if (
          existingSession.filename !== file.name ||
          existingSession.totalSize !== file.size ||
          existingSession.bucketId !== bucketId
        ) {
          throw new ApiError(400, "File mismatch with existing session");
        }

        session = existingSession;
        await this.syncSessionWithServer(session.sessionId);
        session = uploadSessionManager.getSession(session.sessionId);
      } else {
        const initResponse = await this.initiateChunkedUpload(
          file.name,
          bucketId,
          file.size,
          folderId,
        );
        uploadInitiated = true;
        session = uploadSessionManager.createSession(
          initResponse.upload_id,
          file.name,
          bucketId,
          file.size,
          initResponse.chunk_size,
          initResponse.expires_at,
          file.type,
        );
      }

      if (!session)
        throw new ApiError(500, "Upload session initialization failed");

      if (abortSignal?.aborted) {
        uploadSessionManager.updateStatus(session.sessionId, "cancelled");
        throw new Error("Upload aborted");
      }

      const handleAbort = () => {
        uploadSessionManager.pauseSession(session!.sessionId);
        if (onStatusChange)
          onStatusChange(
            "paused",
            uploadSessionManager.getSession(session!.sessionId)!,
          );
      };

      if (abortSignal) abortSignal.addEventListener("abort", handleAbort);

      uploadSessionManager.updateStatus(session.sessionId, "uploading");
      if (onStatusChange) onStatusChange("uploading", session);

      let currentPosition = session.lastChunkOffset ?? 0;
      const chunkSize = session.chunkSize;

      while (currentPosition < file.size) {
        const currentSession = uploadSessionManager.getSession(
          session.sessionId,
        );
        if (!currentSession || currentSession.status === "cancelled")
          throw new Error("Upload cancelled");
        if (currentSession.status === "paused")
          throw new Error("Upload paused");

        const remainingBytes = file.size - currentPosition;
        const thisChunkSize = Math.min(chunkSize, remainingBytes);
        const chunk = file.slice(
          currentPosition,
          currentPosition + thisChunkSize,
        );
        const chunkBuffer = await chunk.arrayBuffer();

        const chunkIndex = Math.floor(currentPosition / chunkSize);
        const chunkResponse = await this.retryOperation(
          async () => {
            return this.uploadChunk(
              session!.uploadId,
              chunkIndex,
              thisChunkSize,
              chunkBuffer,
            );
          },
          Math.min(maxRetries, 2),
          500,
          abortSignal,
        );

        uploadSessionManager.updateProgress(
          session.sessionId,
          chunkResponse.uploaded_bytes,
          currentPosition + thisChunkSize,
        );

        const updatedSession = uploadSessionManager.getSession(
          session.sessionId,
        )!;
        if (onProgress) {
          const progress = Math.round(
            (chunkResponse.uploaded_bytes / file.size) * 100,
          );
          onProgress(progress, chunkResponse.uploaded_bytes, updatedSession);
        }

        if (chunkResponse.completed) {
          uploadSessionManager.completeSession(
            session.sessionId,
            chunkResponse.file_id,
          );
          if (onStatusChange)
            onStatusChange(
              "completed",
              uploadSessionManager.getSession(session.sessionId)!,
            );

          return {
            file_id: chunkResponse.file_id!,
            sessionId: session.sessionId,
          };
        }

        currentPosition += thisChunkSize;
      }

      throw new ApiError(500, "Upload completed but no file ID received");
    } catch (error) {
      if (error instanceof Error) {
        if (
          error.message === "Upload aborted" ||
          error.message === "Upload cancelled"
        ) {
          const currentSession = uploadSessionManager.getSession(
            session?.sessionId ?? "",
          );
          if (currentSession && currentSession.status !== "cancelled") {
            uploadSessionManager.updateStatus(session!.sessionId, "cancelled");
            if (options.onStatusChange) {
              options.onStatusChange(
                "cancelled",
                uploadSessionManager.getSession(session!.sessionId)!,
              );
            }
          }
        } else if (error.message === "Upload paused") {
          return Promise.reject(error);
        } else {
          if (session?.sessionId) {
            uploadSessionManager.updateStatus(
              session.sessionId,
              "error",
              error.message,
            );
            if (options.onStatusChange) {
              options.onStatusChange(
                "error",
                uploadSessionManager.getSession(session.sessionId)!,
              );
            }
          }
        }
      }

      if (uploadInitiated && session?.uploadId) {
        try {
          await this.cancelUpload(session.uploadId);
        } catch {
          // ignore
        }
      }

      throw error;
    }
  }

  // Pause/resume/cancel helpers
  async pauseUpload(sessionId: string): Promise<boolean> {
    return uploadSessionManager.pauseSession(sessionId);
  }

  async resumeUpload(
    sessionId: string,
    file: File,
    bucketId: number,
    options: {
      onProgress?: (
        progress: number,
        uploadedBytes: number,
        session: UploadSession,
      ) => void;
      onStatusChange?: (
        status: UploadSession["status"],
        session: UploadSession,
      ) => void;
      abortSignal?: AbortSignal;
    } = {},
  ): Promise<{ file_id: number; sessionId: string }> {
    if (!uploadSessionManager.resumeSession(sessionId)) {
      throw new ApiError(400, "Cannot resume upload session");
    }

    return this.uploadFileResumable(file, bucketId, {
      ...options,
      sessionId,
    });
  }

  async cancelUploadSession(sessionId: string): Promise<boolean> {
    const session = uploadSessionManager.getSession(sessionId);
    if (!session) return false;

    try {
      await this.cancelUpload(session.uploadId);
    } catch {
      // ignore
    }

    return uploadSessionManager.cancelSession(sessionId);
  }

  getRecoverableUploadSessions(): UploadSession[] {
    return uploadSessionManager.getActiveSessions();
  }

  getUploadSession(sessionId: string): UploadSession | undefined {
    return uploadSessionManager.getSession(sessionId);
  }

  async syncSessionWithServer(sessionId: string): Promise<void> {
    const session = uploadSessionManager.getSession(sessionId);
    if (!session) return;
    await uploadSessionManager.syncWithServer(sessionId, (uploadId) =>
      this.getUploadStatus(uploadId),
    );
  }

  cleanupUploadSessions(): void {
    // no-op: session manager handles cleanup
  }

  validateFileForRecovery(
    session: UploadSession,
    file: File,
  ): { isValid: boolean; errors: string[] } {
    return uploadSessionManager.validateFileForRecovery(session, file);
  }

  associateFileWithSession(sessionId: string, file: File): boolean {
    return uploadSessionManager.associateFileWithSession(sessionId, file);
  }

  async resumeUploadWithFile(
    sessionId: string,
    file: File,
    bucketId: number,
    options: {
      onProgress?: (
        progress: number,
        uploadedBytes: number,
        session: UploadSession,
      ) => void;
      onStatusChange?: (
        status: UploadSession["status"],
        session: UploadSession,
      ) => void;
      abortSignal?: AbortSignal;
    } = {},
  ): Promise<{ file_id: number; sessionId: string }> {
    const session = uploadSessionManager.getSession(sessionId);
    if (!session) throw new ApiError(404, "Upload session not found");

    const validation = this.validateFileForRecovery(session, file);
    if (!validation.isValid) {
      throw new ApiError(
        400,
        `File validation failed: ${validation.errors.join(", ")}`,
      );
    }

    if (!this.associateFileWithSession(sessionId, file)) {
      throw new ApiError(400, "Failed to associate file with session");
    }

    return this.resumeUpload(sessionId, file, bucketId, options);
  }

  // Download & preview helpers
  getFileDownloadUrl(fileId: number): string {
    return `${API_BASE_URL}/v1/files/${fileId}/download`;
  }

  async downloadFile(fileId: number, filename?: string): Promise<void> {
    // Step 1: Get presigned URL from backend
    // This authenticates the user and checks permissions via JWT/cookies
    const presignedResponse = await this.request<PresignedUrlResponse>(
      `/v1/files/${fileId}/presigned-url`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );

    // Step 2: Download using presigned URL
    // The presigned URL contains signature and expiration in the query params
    // No cookies needed - the signature proves authentication
    const presignedUrl = `${API_BASE_URL}${presignedResponse.download_url}`;

    // Use anchor tag with direct URL (no blob conversion)
    // Browser downloads file via Content-Disposition: attachment header
    const link = document.createElement('a');
    link.href = presignedUrl;
    // Note: download attribute doesn't work cross-origin, but Content-Disposition header does
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  }

  async getFileBlob(fileId: number): Promise<Blob> {
    const url = `${this.getFileDownloadUrl(fileId)}?preview=true`;
    const headers: HeadersInit = {
      'X-Request-Type': 'preview', // Tell server this is not a user-initiated download
    };
    
    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }
    
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers,
    });

    if (!response.ok)
      throw new ApiError(
        response.status,
        `File fetch failed: ${response.statusText}`,
      );
    return response.blob();
  }

  async getFileDataUrl(fileId: number): Promise<string> {
    const blob = await this.getFileBlob(fileId);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(blob);
    });
  }

  isPreviewableFile(file: FileRecord): boolean {
    const previewableTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      "video/mp4",
      "video/webm",
      "video/ogg",
      "audio/mpeg",
      "audio/ogg",
      "audio/wav",
      "audio/mp3",
      "application/pdf",
      "text/plain",
      "text/html",
      "text/css",
      "text/javascript",
      "application/json",
      "application/xml",
    ];

    return (
      previewableTypes.includes(file.mime_type || "") ||
      this.isPreviewableByExtension(file.filename)
    );
  }

  private isPreviewableByExtension(filename: string): boolean {
    const ext = filename.toLowerCase().split(".").pop() || "";
    const previewableExts = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "svg",
      "mp4",
      "webm",
      "ogg",
      "mov",
      "mp3",
      "wav",
      "ogg",
      "aac",
      "pdf",
      "txt",
      "md",
      "json",
      "xml",
      "html",
      "css",
      "js",
      "ts",
      "jsx",
      "tsx",
      "py",
      "java",
      "cpp",
      "c",
      "h",
      "rs",
      "go",
      "php",
      "rb",
      "sh",
    ];

    return previewableExts.includes(ext);
  }

  getPreviewType(
    file: FileRecord,
  ): "image" | "video" | "audio" | "pdf" | "text" | "unsupported" {
    const mimeType = file.mime_type || "";
    const ext = file.filename.toLowerCase().split(".").pop() || "";

    if (
      mimeType.startsWith("image/") ||
      ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)
    )
      return "image";
    if (
      mimeType.startsWith("video/") ||
      ["mp4", "webm", "ogg", "mov"].includes(ext)
    )
      return "video";
    if (
      mimeType.startsWith("audio/") ||
      ["mp3", "wav", "ogg", "aac"].includes(ext)
    )
      return "audio";
    if (mimeType === "application/pdf" || ext === "pdf") return "pdf";
    if (
      mimeType.startsWith("text/") ||
      [
        "txt",
        "md",
        "json",
        "xml",
        "html",
        "css",
        "js",
        "ts",
        "jsx",
        "tsx",
        "py",
        "java",
        "cpp",
        "c",
        "h",
        "rs",
        "go",
        "php",
        "rb",
        "sh",
      ].includes(ext)
    )
      return "text";

    return "unsupported";
  }

  async getFileTextContent(fileId: number): Promise<string> {
    const blob = await this.getFileBlob(fileId);
    return blob.text();
  }

  // Avatar upload functionality
  async uploadAvatar(file: File): Promise<{ avatar_url: string; message: string }> {
    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select an image file (JPG, PNG, GIF)');
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      throw new Error('Image must be smaller than 5MB');
    }

    const formData = new FormData();
    formData.append('avatar', file);

    // Use fetch directly instead of request() because we need multipart/form-data
    const url = `${API_BASE_URL}/auth/avatar/upload`;
    const headers: HeadersInit = {};
    
    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      const errorMessage = `Avatar upload failed: ${response.statusText}${errorText ? ` - ${errorText}` : ''}`;
      throw new ApiError(response.status, errorMessage);
    }

    return response.json();
  }

  async deleteAvatar(): Promise<void> {
    // For now, we don't have a delete avatar endpoint, so we'll upload a placeholder
    // In a real implementation, you'd add a DELETE /auth/avatar endpoint
    throw new Error('Avatar deletion not implemented yet');
  }

  // User Management endpoints
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role_id?: number;
    search?: string;
  }): Promise<{
    success: boolean;
    data: {
      users: Array<{
        id: number;
        email: string;
        is_admin: boolean;
        is_locked: boolean;
        role_id?: number;
        role_name?: string;
        created_at: string;
        last_login_at?: string;
      }>;
      total: number;
      page: number;
      limit: number;
      total_pages: number;
    };
    message: string;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.role_id) query.set("role_id", String(params.role_id));
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    return this.request(`/auth/users${qs ? `?${qs}` : ""}`);
  }

  async createUser(data: {
    email: string;
    password: string;
    role_id?: number;
    is_admin?: boolean;
  }): Promise<{
    success: boolean;
    data: {
      id: number;
      email: string;
      is_admin: boolean;
      is_locked: boolean;
      role_id?: number;
      role_name?: string;
      created_at: string;
      last_login_at?: string;
    };
    message: string;
  }> {
    return this.request("/auth/users", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateUser(
    id: number,
    data: {
      email?: string;
      role_id?: number;
      is_admin?: boolean;
      is_locked?: boolean;
    }
  ): Promise<{
    success: boolean;
    data: {
      id: number;
      email: string;
      is_admin: boolean;
      is_locked: boolean;
      role_id?: number;
      role_name?: string;
      created_at: string;
      last_login_at?: string;
    };
    message: string;
  }> {
    return this.request(`/auth/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/auth/users/${id}`, {
      method: "DELETE",
    });
  }

  async getRoles(): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      description?: string;
      created_at: string;
    }>;
    message: string;
  }> {
    return this.request("/auth/roles");
  }

  // API Key Management endpoints
  async getApiKeys(): Promise<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      prefix: string;
      environment: string;
      scopes: string[];
      last_used_at?: string | null;
      expires_at?: string | null;
      created_at: string;
    }>;
    message: string;
  }> {
    const res = await this.request<{
      success: boolean;
      data: Array<Record<string, unknown>>;
      message: string;
    }>(
      "/auth/api-keys",
    );

    const data: Array<{
      id: number;
      name: string;
      prefix: string;
      environment: string;
      scopes: string[];
      last_used_at?: string | null;
      expires_at?: string | null;
      created_at: string;
    }> = [];

    // The response is wrapped: { success, data: [...], message }
    const apiKeys = res.data;

    if (Array.isArray(apiKeys)) {
      for (const k of apiKeys) {
        if (k && typeof k === "object") {
          const r = k as Record<string, unknown>;
          const id = typeof r.id === "number" ? r.id : undefined;
          const key_prefix = typeof r.key_prefix === "string" ? r.key_prefix : undefined;
          const name = typeof r.name === "string" ? r.name : "";
          const environment = typeof r.environment === "string" ? r.environment : "live";
          const scopes = Array.isArray(r.scopes) ? r.scopes.filter((s): s is string => typeof s === "string") : [];
          const created_at = typeof r.created_at === "string" ? r.created_at : undefined;
          const last_used_at = typeof r.last_used_at === "string" ? r.last_used_at : null;
          const expires_at = typeof r.expires_at === "string" ? r.expires_at : null;

          if (id !== undefined && key_prefix && created_at) {
            data.push({ id, name, prefix: key_prefix, environment, scopes, last_used_at, expires_at, created_at });
          }
        }
      }
    }

    return { success: res.success, data, message: res.message };
  }

  async createApiKey(data: {
    name: string;
    scopes: string[];
    environment: string;
    expires_at?: string; // datetime-local from UI
  }): Promise<{
    success: boolean;
    data: {
      id: number;
      name: string;
      key: string; // full secret, shown once
      prefix: string;
      expires_at?: string | null;
      created_at: string;
    };
    message: string;
  }> {
    // Convert expires_at (datetime) to expires_in_days expected by server
    const payload: {
      name: string;
      scopes: string[];
      environment: string;
      expires_in_days?: number;
    } = {
      name: data.name,
      scopes: data.scopes,
      environment: data.environment,
    };

    if (data.expires_at) {
      const expiresAt = new Date(data.expires_at);
      const now = new Date();
      const diffMs = expiresAt.getTime() - now.getTime();
      const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      payload.expires_in_days = isFinite(days) ? days : undefined;
    }

    const res = await this.request<{
      success: boolean;
      data: {
        key: string;
        info: Record<string, unknown>;
      };
      message: string;
    }>(
      "/auth/api-keys",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    // Response is wrapped: { success, data: { key, info }, message }
    let mapped = {
      id: 0,
      name: "",
      key: "",
      prefix: "",
      created_at: new Date().toISOString(),
      expires_at: null as string | null,
    };

    const key = typeof res.data.key === "string" ? res.data.key : "";
    const info = res.data.info && typeof res.data.info === "object" ? res.data.info : undefined;

    if (info) {
      const id = typeof info.id === "number" ? info.id : 0;
      const name = typeof info.name === "string" ? info.name : "";
      const prefix = typeof info.key_prefix === "string" ? info.key_prefix : "";
      const created_at = typeof info.created_at === "string" ? info.created_at : new Date().toISOString();
      const expires_at = typeof info.expires_at === "string" ? info.expires_at : null;
      mapped = { id, name, key, prefix, created_at, expires_at };
    }

    return { success: res.success, data: mapped, message: res.message };
  }

  async deleteApiKey(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/auth/api-keys/${id}`, {
      method: "DELETE",
    });
  }

  async getApiKeyStats(): Promise<{
    success: boolean;
    data: {
      total_keys: number;
      active_keys: number;
      expired_keys: number;
      recent_usage: number;
    };
    message: string;
  }> {
    const res = await this.request<{ success: boolean; data: unknown; message: string }>(
      "/auth/api-keys/stats",
    );
    let total = 0;
    let active = 0;
    let lastUsedStr: string | undefined = undefined;
    if (res.data && typeof res.data === "object") {
      const d = res.data as Record<string, unknown>;
      total = typeof d.total_count === "number" ? d.total_count : 0;
      active = typeof d.active_count === "number" ? d.active_count : 0;
      lastUsedStr = typeof d.last_used === "string" ? d.last_used : undefined;
    }
    let recentUsage = 0;
    if (lastUsedStr) {
      const lastUsed = new Date(lastUsedStr).getTime();
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      recentUsage = lastUsed >= sevenDaysAgo ? 1 : 0;
    }
    return {
      success: res.success,
      data: {
        total_keys: total,
        active_keys: active,
        expired_keys: Math.max(0, total - active),
        recent_usage: recentUsage,
      },
      message: res.message,
    };
  }

  // Access Management endpoints
  async getBucketAccess(bucketId: number): Promise<Array<{
    user_id: number;
    email: string;
    avatar_url?: string;
    access_type: string;
    granted_at?: string;
    granted_by?: number;
  }>> {
    return this.request(`/v1/buckets/${bucketId}/access`);
  }

  async grantBucketAccess(bucketId: number, userEmail: string): Promise<void> {
    return this.request(`/v1/buckets/${bucketId}/access`, {
      method: "POST",
      body: JSON.stringify({ user_email: userEmail }),
    });
  }

  async revokeBucketAccess(bucketId: number, userId: number): Promise<void> {
    return this.request(`/v1/buckets/${bucketId}/access/${userId}`, {
      method: "DELETE",
    });
  }

  async searchUsers(query: string): Promise<Array<{
    user_id: number;
    email: string;
    avatar_url?: string;
    is_admin: boolean;
  }>> {
    const params = new URLSearchParams({ q: query });
    return this.request(`/v1/users/search?${params}`);
  }

  // Password Generation endpoints
  async generatePassword(userId: number): Promise<{
    success: boolean;
    data: {
      user_id: number;
      email: string;
      password: string;
      generated_at: string;
    };
    message: string;
  }> {
    return this.request(`/auth/users/${userId}/generate-password`, {
      method: "POST",
    });
  }

  // User Impersonation endpoints
  async startImpersonation(userId: number): Promise<{
    impersonating_user_id: number;
    impersonating_email: string;
    original_admin_id: number;
    original_admin_email: string;
    started_at: string;
  }> {
    return this.request(`/auth/impersonate/${userId}`, {
      method: "POST",
    });
  }

  async stopImpersonation(): Promise<void> {
    return this.request("/auth/impersonate/stop", {
      method: "POST",
    });
  }

  async getImpersonationStatus(): Promise<{
    is_impersonating: boolean;
    impersonating_user_id?: number;
    impersonating_email?: string;
    original_admin_id?: number;
    original_admin_email?: string;
  }> {
    return this.request("/auth/impersonate/status");
  }

  // File operations
  async moveFile(fileId: number, folderId: number | null): Promise<FileRecord> {
    return this.request(`/v1/files/${fileId}/move`, {
      method: "PUT",
      body: JSON.stringify({ folder_id: folderId }),
    });
  }

  async renameFile(fileId: number, newName: string): Promise<FileRecord> {
    return this.request(`/v1/files/${fileId}/rename`, {
      method: "PUT",
      body: JSON.stringify({ filename: newName }),
    });
  }

  // File deletion endpoints
  async deleteFile(fileId: number): Promise<void> {
    return this.request(`/files/${fileId}`, {
      method: "DELETE",
    });
  }

  async bulkDeleteFiles(fileIds: number[]): Promise<{
    deleted: number[];
    failed: Array<{ file_id: number; error: string }>;
    total_deleted: number;
    total_failed: number;
  }> {
    return this.request("/v1/files/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ file_ids: fileIds }),
    });
  }

  // 2FA / TOTP Management endpoints
  async get2FAStatus(): Promise<{
    success: boolean;
    data: {
      enabled: boolean;
      enabled_at?: string | null;
    };
    message: string;
  }> {
    return this.request("/auth/2fa/status");
  }

  async initiate2FAEnrollment(): Promise<{
    success: boolean;
    data: {
      secret: string;
      qr_code_svg: string;
      backup_codes: string[];
    };
    message: string;
  }> {
    return this.request("/auth/2fa/enroll", {
      method: "POST",
    });
  }

  async verify2FAEnrollment(code: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request("/auth/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  async disable2FA(code: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request("/auth/2fa/disable", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  async verify2FASession(code: string): Promise<{
    success: boolean;
    data?: {
      token?: string;
      verified?: boolean;
    };
    message: string;
  }> {
    return this.request("/auth/2fa/verify-session", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  // Trash Management endpoints
  async listTrash(params?: {
    page?: number;
    limit?: number;
    bucket_id?: number;
  }): Promise<{
    files: FileRecord[];
    total: number;
    page: number;
    limit: number;
  }> {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.bucket_id) query.set("bucket_id", String(params.bucket_id));
    const qs = query.toString();
    return this.request(`/v1/files/trash${qs ? `?${qs}` : ""}`);
  }

  async restoreFile(fileId: number): Promise<FileRecord> {
    return this.request(`/v1/files/trash/${fileId}/restore`, {
      method: "POST",
    });
  }

  async bulkRestoreFiles(fileIds: number[]): Promise<{
    restored: number[];
    not_found: number[];
    failed: Array<{ file_id: number; error: string }>;
    total_restored: number;
    total_not_found: number;
    total_failed: number;
  }> {
    return this.request("/v1/files/trash/bulk-restore", {
      method: "POST",
      body: JSON.stringify({ file_ids: fileIds }),
    });
  }

  async permanentlyDeleteFiles(fileIds: number[]): Promise<{
    deleted: number[];
    not_found: number[];
    failed: Array<{ file_id: number; error: string }>;
    total_deleted: number;
    total_not_found: number;
    total_failed: number;
  }> {
    return this.request("/v1/files/trash/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ file_ids: fileIds }),
    });
  }
}

export const api = new ApiService();
export { ApiError };
